import React, { useState } from 'react';
import { CheckCircle2, Plus, Sparkles, X } from 'lucide-react';
import type { IntlLike } from '../clientPresentation';
import { recruitProfileLabelId } from './RecruitProfileChips';
import { DISC_ARCHETYPES } from '../../../CareerQuiz/careerDiscArchetypes';
import { quizLangOf } from '../../../CoverQuiz/quizLang';
import { DISC_EMOJI, isDiscLetter } from '../../../../constants/disc';
import {
  DiscPersonaCard,
  IdeaOpener,
  TypewriterText
} from '../../../../components/ClientEntry/SmartIdeaDialog/IdeaKit';

// Shared with Smart IDEA now; re-exported for the callers that import it from here.
export { TypewriterText };

/**
 * 招募 IDEA's "fun layer" — the pieces that keep an I-type (孔雀) adviser
 * reading: a punchy opener card, a game-style persona card, a storyline and
 * a mascot. Everything here is a lookup over data the dialog already has
 * (DISC letter, 痛點/顧慮/優勢 picks) — nothing is generated, nothing calls
 * an API, nothing new is stored.
 */

/** The DISC animal cast — the same four the candidate quiz plays with. */
const DISC_ANIMALS: Record<string, { emoji: string }> = {
  D: { emoji: DISC_EMOJI.D },
  I: { emoji: DISC_EMOJI.I },
  S: { emoji: DISC_EMOJI.S },
  C: { emoji: DISC_EMOJI.C }
};

/**
 * ① 建議開場 — the one line the adviser walks in with. The pool is the
 * candidate's DISC openers followed by one probe per pain point on file;
 * 換一句 cycles it (a remount replays the typewriter). I-types don't read nine
 * sections — they want one line that sounds good out loud.
 *
 * Renders no card of its own: it heads the DISC 對話建議 section's 建議開場
 * box (it used to be a separate 今日開場白 card above it), so the host passes
 * the box's title for the header row that also carries 換一句.
 */
export type RecruitOpenerProps = {
  title: string;
  candidateName: string;
  discLetter?: string | null;
  painPoints: string[];
  intl: IntlLike;
};

export const RecruitOpener = (props: RecruitOpenerProps) => {
  const { title, candidateName, discLetter, painPoints, intl } = props;
  const t = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);
  const lines: string[] = [
    ...(discLetter && DISC_ANIMALS[discLetter] ? t(`clientv2-recruit-disc-${discLetter}-ask`).split('\n') : []),
    ...painPoints.map(key => t(`clientv2-recruit-pain-${key}-probe`))
  ].filter(Boolean);
  return (
    <IdeaOpener
      title={title}
      lead={t('clientv2-recruit-talkcard-line', { name: candidateName })}
      lines={lines}
      fallback={t('clientv2-recruit-talkcard-fallback')}
      refreshLabel={t('clientv2-recruit-talkcard-refresh')}
    />
  );
};

/**
 * ③ 候選人角色卡 — the candidate as a game character: animal avatar, rarity
 * badge, three DISC communication tendencies and strength tags. Tendencies are
 * qualitative lookup labels, not invented ability scores.
 */
export type RecruitPersonaCardProps = {
  discLetter?: string | null;
  strengths: string[];
  intl: IntlLike;
  /** Opens the DISC picker. The persona card is where 招募 IDEA sets the type. */
  onSetType?: () => void;
  /** Starts editing 優勢 (他做得來嗎) when none are on file. */
  onAddStrengths?: () => void;
  /** 生日參考 — the host's DobReadings, shown at the foot of the card. */
  readings?: React.ReactNode;
};

const TENDENCY_KEYS = [1, 2, 3] as const;

export const RecruitPersonaCard = (props: RecruitPersonaCardProps) => {
  const { discLetter, strengths, intl, onSetType, onAddStrengths, readings } = props;
  const t = (id: string, values?: Record<string, unknown>) => {
    try {
      const msg = intl.formatMessage({ id }, values);
      return msg || id;
    } catch {
      return id;
    }
  };
  const cast = discLetter ? DISC_ANIMALS[discLetter] : undefined;
  const lang = quizLangOf(intl.locale);
  const archetype = discLetter && isDiscLetter(discLetter) ? DISC_ARCHETYPES[discLetter] : undefined;
  const hasStrengths = strengths && strengths.length > 0;

  return (
    <DiscPersonaCard
      testId="recruit-persona"
      letter={cast ? discLetter : null}
      emoji={archetype?.emoji || cast?.emoji}
      name={archetype ? archetype.name[lang] : 'DISC'}
      color={archetype?.color}
      tagline={archetype?.tagline[lang]}
      traits={archetype?.traits.map(trait => trait[lang])}
      tendencies={
        discLetter ? TENDENCY_KEYS.map(key => t(`clientv2-recruit-persona-tendency-${discLetter}-${key}`)) : []
      }
      tendencyTestId="recruit-persona-tendency"
      readings={readings}
      readingsTitle={t('clientv2-recruit-persona-dob')}
      onSetType={onSetType}
      editLabel={t('clientv2-recruit-profile-edit')}
      setTypeLabel={t('choose-personality')}
      emptyText={t('clientv2-recruit-persona-empty')}
    >
      <div className="clientv2-persona-tags-box">
        <span className="clientv2-persona-tags-title">{t('clientv2-recruit-persona-tags-title')}</span>
        {hasStrengths ? (
          <div className="clientv2-persona-tags">
            {strengths.slice(0, 4).map(key => (
              <span key={key} className="clientv2-persona-tag" data-testid="recruit-persona-tag">
                <CheckCircle2 size={12} aria-hidden="true" />
                {t(recruitProfileLabelId('strengths', key))}
              </span>
            ))}
          </div>
        ) : (
          <button
            type="button"
            className="clientv2-idea-empty"
            data-testid="recruit-persona-add-strengths"
            disabled={!onAddStrengths}
            onClick={onAddStrengths}
          >
            <Plus size={13} aria-hidden="true" />
            {t('clientv2-recruit-profile-empty-strengths')}
          </button>
        )}
      </div>
    </DiscPersonaCard>
  );
};

/**
 * ⑥ 吉祥物 — a little animal that bobs in the corner and holds one tailored
 * tip. Clicking it replays its pop (that is the whole interaction — a mascot
 * is company, not homework); the ✕ shelves it until the dialog remounts.
 */
export type RecruitMascotProps = {
  discLetter?: string | null;
  intl: IntlLike;
};

export const RecruitMascot = (props: RecruitMascotProps) => {
  const { discLetter, intl } = props;
  const t = (id: string) => intl.formatMessage({ id });
  const [dismissed, setDismissed] = useState(false);
  const [pop, setPop] = useState(0);
  if (dismissed) return null;
  const cast = discLetter ? DISC_ANIMALS[discLetter] : undefined;
  const tip = cast ? t(`clientv2-recruit-mascot-tip-${discLetter}`) : t('clientv2-recruit-mascot-tip-generic');
  return (
    <div className="clientv2-mascot" data-testid="recruit-mascot">
      <div className="clientv2-mascot-bubble">
        <TypewriterText text={tip} speed={16} />
        <button
          type="button"
          className="clientv2-mascot-dismiss"
          data-testid="recruit-mascot-dismiss"
          aria-label={t('clientv2-recruit-mascot-dismiss')}
          onClick={() => setDismissed(true)}
        >
          <X size={11} aria-hidden="true" />
        </button>
      </div>
      <button
        key={pop}
        type="button"
        className="clientv2-mascot-animal"
        data-testid="recruit-mascot-animal"
        aria-label={t('clientv2-recruit-mascot-title')}
        onClick={() => setPop(p => p + 1)}
      >
        {cast?.emoji || '🐣'}
      </button>
    </div>
  );
};
